/**
 * LinkBehavior — MUI's `href` prop bridge to react-router's `<Link to=...>`.
 *
 * Per public-website design §3.2, the SPA's MUI theme registers this
 * component as the `defaultProps.component` for `MuiLink` and the
 * `defaultProps.LinkComponent` for `MuiButtonBase`. Every `<MuiLink href>`
 * and link-bearing `<MuiButton href>` invocation therefore routes through
 * react-router instead of issuing a full-page navigation. The pattern is
 * the canonical one from the MUI routing-libraries doc.
 *
 * The component:
 * 1. Accepts MUI's `href` prop.
 * 2. Forwards every other prop to react-router's `Link`.
 * 3. Translates `href` → `to`.
 * 4. Forwards the `ref` so MUI's auto-focus and analytics hooks work.
 */
import * as React from 'react';
import { Link as RouterLink, type LinkProps as RouterLinkProps } from 'react-router-dom';

/**
 * Props accepted by `LinkBehavior`. Identical to react-router's
 * `LinkProps` minus `to` (which we derive from MUI's `href`) and plus
 * `href` (which MUI passes).
 */
export interface LinkBehaviorProps extends Omit<RouterLinkProps, 'to'> {
  /** MUI passes the destination URL via `href`; we map it to `to`. */
  readonly href: RouterLinkProps['to'];
}

/**
 * `forwardRef` is required so MUI's auto-focus / analytics layers can
 * still grab a handle on the underlying anchor element.
 */
export const LinkBehavior = React.forwardRef<HTMLAnchorElement, LinkBehaviorProps>(
  function LinkBehavior(props, ref) {
    const { href, ...other } = props;
    return <RouterLink ref={ref} to={href} {...other} />;
  }
);
